import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { User } from "@common/types/user";
import { useMemo } from "react";
import { NoValueIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { getTaskAssigneeDisplayName } from "../task-assignee.lib";

interface TaskDetailAssigneeSelectProps {
    assigneeId: User["id"] | null | undefined;
    onValueChange: (assigneeId: User["id"] | null) => Promise<unknown> | void;
    triggerClassName: string;
}

export const TaskDetailAssigneeSelect = ({
    assigneeId,
    onValueChange,
    triggerClassName,
}: TaskDetailAssigneeSelectProps) => {
    const { data: members = [], isFetched: membersFetched } =
        useCurrentWorkspaceMembers();

    const selectedAssignee = useMemo(
        () => members.find((member) => member.user.id === assigneeId),
        [members, assigneeId],
    );

    return (
        <Picker.Select
            value={assigneeId ?? null}
            onValueChange={(nextAssigneeId) => {
                void onValueChange(nextAssigneeId);
            }}
        >
            <Picker.ButtonTrigger
                render={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        block={false}
                        className={triggerClassName}
                        aria-label={
                            selectedAssignee
                                ? getTaskAssigneeDisplayName(
                                      selectedAssignee.user,
                                  )
                                : undefined
                        }
                    />
                }
                value={
                    selectedAssignee ? (
                        <HStack gap={8} align="center">
                            <UserAvatar
                                user={selectedAssignee.user}
                                size="sm"
                                aria-hidden
                            />
                            {getTaskAssigneeDisplayName(selectedAssignee.user)}
                        </HStack>
                    ) : (
                        "Assign member"
                    )
                }
                placeholder="Assign member"
            />
            <Picker.Popup sideOffset={4}>
                <Picker.List>
                    <Picker.Item
                        leading={<NoValueIcon />}
                        onSelect={() => {
                            void onValueChange(null);
                        }}
                    >
                        No assignee
                    </Picker.Item>
                    {members.map((member) => (
                        <Picker.Item
                            key={member.user.id}
                            value={member.user.id}
                            leading={
                                <UserAvatar
                                    user={member.user}
                                    size="xs"
                                    aria-hidden
                                />
                            }
                        >
                            {getTaskAssigneeDisplayName(member.user)}
                        </Picker.Item>
                    ))}
                    {!membersFetched && (
                        <Picker.Loading>Loading...</Picker.Loading>
                    )}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
